import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Assignment } from 'src/app/classes/assignment';
import { SettingsService } from 'src/app/services/settings.service';
import { v4 as uuidv4 } from 'uuid';
import * as fa from '@fortawesome/free-solid-svg-icons';
import { Capacitor } from '@capacitor/core';
import { AiService } from 'src/app/services/ai.service';
import { Prompt } from 'src/app/classes/prompt';
import { SalaryRulesComponent } from './salary-rules/salary-rules.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Product } from 'src/app/classes/product';
import productUnits from 'src/app/constants/product-units';
import { FormGroup } from '@angular/forms';
import { FormControl, Validators } from '@angular/forms';
import { Customer } from 'src/app/classes/customer';
import { ProductComponent } from './product/product.component';
import { FeedbackService } from 'src/app/services/feedback.service';
import { Feedback } from 'src/app/classes/feedback';
import { TranslateFieldPipe } from 'src/app/pipes/translate-field.pipe';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.scss'],
  providers: [TranslateFieldPipe]
})
export class ServicesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('promptInput') promptInput!: ElementRef
  @ViewChild('productRequest') productRequest!: ElementRef

  assignment = new Assignment()
  assginmentLoaded = false
  animatedDots = ''
  agentResponse = ''
  agentResponseProducts: any[] = []
  agentReponseAssignments: Assignment[] = []
  assignments: Assignment[] = []
  assignmentsAll: Assignment[] = []
  fa = fa
  feedbacks: Feedback[] = []
  firstPromptSent = false
  intervalId: any
  isTyping = false
  isMobile = /android|iphone|ipad|ipod|windows phone/i.test(navigator.userAgent)
  lastPrompt = ''
  now = new Date()
  platform = Capacitor.getPlatform()
  placeholderText = $localize`What do you need done? Just tell me.`
  prompt!: Prompt
  products: Product[] = []
  productsAll: Product[] = []
  productsLoaded = false
  productUnits = productUnits
  orderProducts: any[] = []

  shiftForm = new FormGroup({
    date: new FormControl<null | string>(null, [Validators.required]),
    start_times: new FormControl<null | string[]>(null),
    assignment_id: new FormControl<null | number>(null, [Validators.required]),
    customer: new FormControl<null | Customer>(null, [Validators.required]),
    employee_id: new FormControl<null | number>(null),
    products: new FormControl<any>([]),
    notes: new FormControl('', { nonNullable: true }),
    discount_id: new FormControl<null | number>(null),
  })

  constructor(
    private settingsService: SettingsService,
    private feedbackService: FeedbackService,
    private route: ActivatedRoute,
    public router: Router,
    private aiService: AiService,
    private modalService: NgbModal,
    public translateField: TranslateFieldPipe,
    private titleService: Title
  ) { }

  ngOnInit() {
    window.scrollTo(0, 0)
    
    // No chat ID, generate one
    if (!localStorage.getItem('sales_chat_id'))
      localStorage.setItem('sales_chat_id', uuidv4());

    this.route.params.subscribe(params => {
      this.settingsService.assignmentPublicBySlug(params['slug']).subscribe(assignments => {
        if (assignments && assignments.length > 0) {
          this.assignment = assignments[0]
          this.assginmentLoaded = true

          this.titleService.setTitle(this.translateField.transform(this.assignment, 'name') as string)

          this.settingsService.assignmentProductsPublic(this.assignment.id).subscribe(products => {
            this.products = products
            this.productsAll = products

            this.productsLoaded = true

            if (localStorage.getItem('order')) {
              const order = JSON.parse(localStorage.getItem('order')!)
      
              this.shiftForm.patchValue(order)

              this.updateOrderProducts()
            }

            this.route.queryParams.subscribe(queryParams => {
              if (queryParams['prompt']) {
                // Store the query params except 'prompt'
                const { prompt, ...otherParams } = queryParams;
                
                // Navigate to same route without prompt param
                this.router.navigate([], {
                  queryParams: otherParams,
                  replaceUrl: true
                });

                this.promptInput.nativeElement.innerHTML = queryParams['prompt']

                this.agentSend()
              }

              else {
                this.aiService.prompt('customer_product_sales_agent').subscribe(result => {
                  this.prompt = result as Prompt
                })
              }
            })
          })

          this.route.fragment.subscribe(fragment => {
            if(fragment == 'order') {
              setTimeout(() => {
                this.scrollToOrder()
              }, 500)
            }
          })

          this.feedbackService.assignment(this.assignment.id).subscribe(feedbacks => {
            this.feedbacks = feedbacks
          })
        }

        else {
          alert($localize `Could not find: ` + params['slug'])
          this.router.navigate(['/'])
        }
      })
    })

    // Fetch all asiignments also so we can use them in the agent responses
    this.settingsService.assignmentsPublic().subscribe(assignments => {
      this.assignmentsAll = assignments
    })

    this.intervalId = setInterval(() => {
      this.animatedDots = this.animatedDots.length < 3 ? this.animatedDots + '.' : '';
    }, 250);
  }

  ngAfterViewInit() {
    if(this.platform == 'web') {
      this.promptInput.nativeElement.focus()
    }
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  agentSend() {
    const userPrompt = this.lastPrompt = this.stripHTML(this.promptInput.nativeElement.innerHTML)

    this.promptInput.nativeElement.innerHTML = ''
    this.agentResponse = ''
    this.firstPromptSent = true

    if (userPrompt.length > 0) {
      this.animatedDots = ''
      this.agentReponseAssignments = []
      this.agentResponseProducts = []

      this.aiService.productSalesAssistant(localStorage.getItem('sales_chat_id'), this.assignment.id, userPrompt).subscribe({
        next: (result) => {
          this.now = new Date()

          this.agentResponse = result.response

          this.agentReponseAssignments = result.assignments.map((assignment: Assignment) => this.assignmentsAll.find(a => a.id == assignment.id)!)
          this.agentResponseProducts = result.services

          if (result.services.length) {
            this.aiFillProducts(result.services)
          }
        },
        error: () => {
          this.agentResponse = $localize`I'm sorry, I couldn't process your request. Please try again.`
        }
      })
    }

    else {
      this.now = new Date()
      this.agentResponse = $localize`Please enter a message.`
    }
  }

  getProduct(product_id: number) {
    return this.productsAll.find(p => p.id == product_id)
  }

  addAiProduct(aiResult: any) {
    const modal = this.modalService.open(ProductComponent, { centered: true, fullscreen: 'lg' })

    modal.componentInstance.assignment = this.assignment
    modal.componentInstance.product = this.products.find(p => p.id === aiResult.product_id)
    modal.componentInstance.values = aiResult
    modal.componentInstance.fromAi = true
    modal.componentInstance.lastPrompt = this.lastPrompt

    modal.result.then((result) => {
      if (result) {
        if(!this.shiftForm.controls.assignment_id.value) {
          this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
        }
        else if(this.shiftForm.controls.assignment_id.value != this.assignment.id) {
          if(confirm($localize`You can order services from one category at a time. Are you sure you want to change the category?`)) {
            this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
            this.shiftForm.controls.products.setValue([])
            this.updateOrderProducts()
          }
          else {
            return
          }
        }

        if (!this.shiftForm.controls.products.value) {
          this.shiftForm.controls.products.setValue([])
        }

        if (this.shiftForm.controls.products.value.find((p: any) => p.product_id == result.product_id)) {
          const orderProduct = this.shiftForm.controls.products.value.find((p: any) => p.product_id == result.product_id)
          orderProduct.number = result.number
        }

        else {
          this.shiftForm.controls.products.value.push(result)
        }

        this.updateOrderProducts()

        if(result.next) {
          this.router.navigate(['/schedule'])
        }
      }
    })
  }

  aiFillProducts(services: any[]) {
    this.products = this.productsAll.filter(product => services.find(prod => prod.product_id == product.id))
  }

  selectAssignmentWithPrompt(assignment: Assignment) {
    this.router.navigate(['/services', this.translateField.transform(assignment, 'slug')], { queryParams: { prompt: this.lastPrompt } })
  }

  productRequestSend() {
    if (this.productRequest.nativeElement.value == '')
      return

    this.settingsService.productRequestCreate(this.productRequest.nativeElement.value).subscribe(() => {
      alert($localize`Request sent. Thank you.`)
      this.productRequest.nativeElement.value = ''
    })
  }

  orderProduct(product: Product | undefined) {
    const modal = this.modalService.open(ProductComponent, { centered: true, fullscreen: 'lg' })

    modal.componentInstance.assignment = this.assignment
    modal.componentInstance.product = product
    modal.componentInstance.values = this.orderProducts.find(p => p.product.id == product?.id)
    modal.componentInstance.lastPrompt = this.lastPrompt

    modal.result.then((result) => {
      if (result) {
        if(!this.shiftForm.controls.assignment_id.value) {
          this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
        }
        else if(this.shiftForm.controls.assignment_id.value != this.assignment.id) {
          if(confirm($localize`You can order services from one category at a time. Are you sure you want to change the category?`)) {
            this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
            this.shiftForm.controls.products.setValue([])
            this.updateOrderProducts()
          }
          else {
            return
          }
        }

        if (!this.shiftForm.controls.products.value) {
          this.shiftForm.controls.products.setValue([])
        }

        if (this.shiftForm.controls.products.value.find((p: any) => p.product_id == result.product_id)) {
          const orderProduct = this.shiftForm.controls.products.value.find((p: any) => p.product_id == result.product_id)
          orderProduct.number = result.number
        }

        else {
          this.shiftForm.controls.products.value.push(result)
        }

        this.updateOrderProducts()

        if(result.next) {
          this.router.navigate(['/schedule'])
        }
      }
    })
  }

  deleteProduct(index: number) {
    if (!confirm($localize`Are you sure you want to remove this item from your order?`)) {
      return
    }

    this.shiftForm.controls.products.value.splice(index, 1)

    if(this.shiftForm.controls.products.value.length == 0) {
      this.shiftForm.controls.assignment_id.setValue(null)
    }

    this.updateOrderProducts()
  }

  editProduct(index: number) {
    const modal = this.modalService.open(ProductComponent, { centered: true, fullscreen: 'lg' })

    modal.componentInstance.assignment = this.assignment
    modal.componentInstance.product = this.productsAll.find(p => p.id === this.orderProducts[index].product.id)
    modal.componentInstance.values = this.orderProducts[index]
    modal.componentInstance.lastPrompt = this.lastPrompt

    modal.result.then((result) => {
      if (result) {
        if(!this.shiftForm.controls.assignment_id.value) {
          this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
        }
        else if(this.shiftForm.controls.assignment_id.value != this.assignment.id) {
          if(confirm($localize`You can order services from one category at a time. Are you sure you want to change the category?`)) {
            this.shiftForm.controls.assignment_id.setValue(this.assignment.id)
            this.shiftForm.controls.products.setValue([])
            this.updateOrderProducts()
          }
          else {
            return
          }
        }

        if (!this.shiftForm.controls.products.value) {
          this.shiftForm.controls.products.setValue([])
        }

        if (result.number < 1) {
          this.shiftForm.controls.products.value.splice(index, 1)
        }

        else {
          this.shiftForm.controls.products.value[index] = result
        }

        this.updateOrderProducts()

        if(result.next) {
          this.router.navigate(['/schedule'])
        }
      }
    })
  }

  scrollToOrder() {
    const element = document.getElementById('your-order');
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - 65;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  salaryRules() {
    const modal = this.modalService.open(SalaryRulesComponent, { centered: true, fullscreen: 'lg', size: 'xl', scrollable: true })

    modal.componentInstance.salaryRulesId = this.assignment.salary_rules_id
  }

  showAllProducts() {
    this.products = [...this.productsAll]
  }

  stripHTML(html: string) {
    // Replace <br> tags with new lines
    const withNewLines = html.replace(/<br\s*\/?>/gi, '\n');
    // Remove all other HTML tags
    const strippedText = withNewLines.replace(/<\/?[^>]+(>|$)/g, '');
    return strippedText.trim();
  }

  updateOrderProducts() {
    this.orderProducts = []

    if (!this.shiftForm.controls.products.value || !Array.isArray(this.shiftForm.controls.products.value)) {
      this.shiftForm.controls.products.setValue([])
    }
    
    for (let product of this.shiftForm.controls.products.value) {
      let productObject = this.productsAll.find(p => p.id == product.product_id)

      if(!productObject) {
        this.settingsService.assignmentProductsPublic(this.shiftForm.controls.assignment_id.value as number).subscribe(products => {
          productObject = products.find((p:any) => p.id == product.product_id)
          
          this.orderProducts.push({ ...product, product: productObject })
        })

        continue
      }

      this.orderProducts.push({ ...product, product: productObject })
    }

    localStorage.setItem('order', JSON.stringify(this.shiftForm.value))
  }
}
